'use client';

import { useCallback, useState } from 'react';

import {
  APIProvider,
  Map as GoogleMap,
  MapCameraChangedEvent,
  MapCameraProps,
  Marker,
} from '@vis.gl/react-google-maps';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';

import { AnimatedText } from '@/blocks/animations/animated-text';
import { Container } from '@/blocks/components/Container';
import { AccentFragment } from '@/blocks/components/HeadingAccentFragment';
import { LinkEmail } from '@/blocks/components/PlatformLink/LinkEmail';
import { CenterHeading, VStack } from '@/blocks/components/layout';
import { emailSupport, siteVersion } from '@/global/public-settings';
import buttons from '@/styles/button.module.scss';
import typography from '@/styles/typography.module.scss';

import styles from './AboutContactUsMapSection.module.scss';

const GoogleMapApiKey = 'AIzaSyBZFJnZ9ku9iW0PTeUAWdGl6a5rm9e7QtI';

const INITIAL_CAMERA = siteVersion.isAfrica
  ? {
      center: { lat: -34.15371674922115, lng: 18.86781561237762 },
      zoom: 15,
    }
  : {
      center: { lat: 14.033916531040122, lng: -60.97346289868576 },
      zoom: 15,
    };

const Map = () => {
  const [cameraProps, setCameraProps] = useState<MapCameraProps>(INITIAL_CAMERA);
  const handleCameraChange = useCallback((ev: MapCameraChangedEvent) => setCameraProps(ev.detail), []);

  return (
    <APIProvider apiKey={GoogleMapApiKey}>
      <GoogleMap {...cameraProps} onCameraChanged={handleCameraChange} className={styles.mapContainer}>
        <Marker position={INITIAL_CAMERA.center} />
      </GoogleMap>
    </APIProvider>
  );
};

export function AboutContactUsMapSection() {
  const tr = useTranslations();

  return (
    <Container tag={'section'}>
      <VStack>
        <CenterHeading>
          <AnimatedText className={typography.h2}>
            {tr.rich('pages.about.contactUs.sections.map.title', {
              span: (content) => <AccentFragment>{content}</AccentFragment>,
            })}
          </AnimatedText>
          <p className={typography.subheadingM}>
            {siteVersion.isAfrica
              ? tr.rich('pages.about.contactUs.sections.map.subheadingAfrica')
              : tr.rich('pages.about.contactUs.sections.map.subheading')}
          </p>
        </CenterHeading>
        <div className={styles.wrapper}>
          <Map />
          <div className={styles.workingInfoWrapper}>
            <div className={styles.workingInfoContainer}>
              <div className={styles.address}>
                <p className={classNames(styles.addressTitle, typography.subheadingML)}>
                  {tr.rich('components.verticalBusinessCard.addressTitle')}
                </p>
                <div className={styles.infoCredits}>
                  <p className={typography.subheadingSM}>
                    {siteVersion.isAfrica
                      ? tr.rich('pages.about.contactUs.sections.map.firstAddressAfrica')
                      : tr.rich('pages.about.contactUs.sections.map.firstAddress')}
                  </p>
                  <p className={typography.subheadingSM}>
                    {siteVersion.isAfrica
                      ? tr.rich('pages.about.contactUs.sections.map.secondAddressAfrica')
                      : tr.rich('pages.about.contactUs.sections.map.secondAddress')}
                  </p>
                  <p className={typography.subheadingSM}>
                    {siteVersion.isAfrica
                      ? tr.rich('pages.about.contactUs.sections.map.thirdAddressAfrica')
                      : tr.rich('pages.about.contactUs.sections.map.thirdAddress')}
                  </p>
                </div>
              </div>

              <div className={styles.workingDays}>
                <p className={classNames(styles.workingDaysTitle, typography.subheadingML)}>
                  {tr.rich('components.verticalBusinessCard.workingDaysTitle')}
                </p>
                <div className={styles.infoCredits}>
                  <p className={typography.subheadingSM}>
                    {tr.rich('pages.about.contactUs.sections.map.workingDays')}
                  </p>
                </div>
              </div>

              <div className={styles.operatingHours}>
                <p className={classNames(styles.operatingHoursTitle, typography.subheadingML)}>
                  {tr.rich('components.verticalBusinessCard.operatingHoursTitle')}
                </p>
                <div className={styles.infoCredits}>
                  <p className={typography.subheadingSM}>
                    {tr.rich('pages.about.contactUs.sections.map.operatingHours')}
                  </p>
                </div>
              </div>

              <div className={styles.email}>
                <p className={classNames(styles.emailTitle, typography.subheadingML)}>
                  {tr.rich('components.verticalBusinessCard.contactsTitle')}
                </p>
                <div className={styles.infoCredits}>
                  <LinkEmail className={classNames(styles.emailLink, buttons.btnLink)} email={emailSupport} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </VStack>
    </Container>
  );
}
